/**
 * Dashboard widget: latest entries from the analytics compliance timeline (events, insights, improvements, risks).
 * Links to /timeline for the full view.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTimeline } from '../../api/analytics';
import { formatComplianceTimelineTitle } from '../../utils/analyticsDocumentLabel';
import { Activity, Lightbulb, TrendingUp, AlertTriangle, ChevronRight } from 'lucide-react';

const ENTRY_ICONS = { event: Activity, insight: Lightbulb, improvement: TrendingUp, risk: AlertTriangle };

function formatTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return diffDays === 1 ? 'Yesterday' : `${diffDays}d ago`;
}

export default function ComplianceTimelineWidget() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTimeline({ page: 1, limit: 5 })
      .then((res) => setEntries(res.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="compliance-timeline-widget">
        <h3>Compliance Timeline</h3>
        <p className="dashboard-widget-loading">Loading…</p>
      </div>
    );
  }

  return (
    <div className="compliance-timeline-widget">
      <h3>Compliance Timeline</h3>
      {entries.length === 0 ? (
        <p className="dashboard-widget-empty">No recent activity. Upload docs or run a snapshot to see events here.</p>
      ) : (
        <ul className="compliance-timeline-widget-list">
          {entries.map((entry) => {
            const Icon = ENTRY_ICONS[entry.type] || Activity;
            return (
              <li key={entry.id} className="compliance-timeline-widget-item">
                <span className="compliance-timeline-widget-icon" aria-hidden>
                  <Icon size={16} />
                </span>
                <button
                  type="button"
                  className="compliance-timeline-widget-entry"
                  onClick={() => entry.actionRoute && navigate(entry.actionRoute)}
                  disabled={!entry.actionRoute}
                >
                  <span className="compliance-timeline-widget-title">
                    {formatComplianceTimelineTitle(entry.title)}
                  </span>
                  <span className="compliance-timeline-widget-meta">{formatTime(entry.timestamp)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button type="button" className="button button--ghost button--sm" onClick={() => navigate('/timeline')}>
        View full timeline <ChevronRight size={14} />
      </button>
    </div>
  );
}
